<template>
    <div class="container-fluid">
        <div class="row justify-content-center">
            <div class="col-12 user-table-col">
                <br />
                <br />
                <div class="row">
                    <div class="col-12">
                        <h1 class="mb-3 font-weight-bolder h4">Users</h1>
                        <p class="text-secondary mb-3">Manage team members and find learners.</p>
                        <div class="mb-3">
                            <ul class="nav">
                                <li
                                    class="nav-item tab-nav tab-nav-highlight mr-2"
                                    role="button"
                                    @click="setUserTypeFilter('team')"
                                >
                                    <div class="nav-link tab-nav-link" :class="{ active: userTypeFilter === 'team' }">
                                        Team
                                    </div>
                                </li>
                                <li
                                    class="nav-item tab-nav tab-nav-highlight mr-1"
                                    role="button"
                                    @click="setUserTypeFilter('learners')"
                                >
                                    <div
                                        class="nav-link tab-nav-link"
                                        :class="{ active: userTypeFilter === 'learners' }"
                                    >
                                        Learners
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div class="row">
                    <div class="col-12" v-show="userTypeFilter === 'team'">
                        <base-table
                            v-model="selectedMembers"
                            :table-data="members"
                            :config="memberTableConfig"
                            @edit="openEditMember($event)"
                            @delete="openDeleteMember($event)"
                            @search="updateSearchQuery($event)"
                            empty-message="No team members to display."
                            :server-side-search-mode="true"
                            :loading="searchLoading && !loading"
                            :pageStart.sync="pageIndexStart"
                            :pageEnd.sync="pageIndexEnd"
                            :sortColumn.sync="sortColumn"
                            :sortDescend.sync="sortDescend"
                            :filtersApplied.sync="filtersApplied"
                            :server-side-total-results="memberTotalSearchResults"
                            :rows-per-page.sync="memberRowsPerPage"
                            :rows-per-page-options="[10, 50, 100]"
                            :max-pages="20"
                            :max-items-per-column="2"
                            ref="memberTable"
                        >
                            <template slot="actions">
                                <button
                                    @click="openStatusModal()"
                                    v-show="showBulkUserActions"
                                    type="button"
                                    class="btn btn-sm btn-gray mr-1 mb-1 mb-sm-0"
                                >
                                    <span class="icon icon-glyphicons-basic-pencil align-middle"></span>
                                </button>
                                <button
                                    @click="openAddToGroup(false)"
                                    v-show="showBulkUserActions"
                                    type="button"
                                    class="btn btn-sm btn-gray mr-1 mb-1 mb-sm-0"
                                >
                                    <span>Remove from ...</span>
                                </button>
                                <button
                                    @click="openAddToGroup(true)"
                                    v-show="showBulkUserActions"
                                    type="button"
                                    class="btn btn-sm btn-gray mr-1 mb-1 mb-sm-0"
                                >
                                    <span>Add to ...</span>
                                </button>
                                <button
                                    @click="openUploadModal()"
                                    :disabled="!userIsAdmin"
                                    type="button"
                                    class="btn btn-sm btn-gray mr-1 mb-1 mb-sm-0"
                                >
                                    <span class="icon icon-lg icon-glyphicons-basic-cloud-upload align-middle"></span>
                                </button>
                                <button
                                    @click="openCreateMember()"
                                    :disabled="!userIsAdmin"
                                    type="button"
                                    class="btn btn-sm btn-primary"
                                >
                                    <span class="d-inline d-xl-none">Add</span>
                                    <span class="d-none d-xl-inline">Add Team Member</span>
                                </button>
                            </template>
                            <template slot="footer">
                                <div v-show="!loading && userIsAdmin" class="mb-1">
                                    <div v-show="legacyUserMgmtActive">
                                        <span class="small text-secondary font-weight-500"
                                            >{{ legacyUserSeatsRemaining }} User Seats Available</span
                                        >
                                    </div>
                                    <div v-show="!legacyUserMgmtActive" class="d-flex align-items-center">
                                        <span class="badge badge-gray font-weight-500 mr-2"
                                            >{{ organizationDetails.active_creator_count }}/{{
                                                organizationDetails.max_creator_users
                                            }}
                                            Authors Added</span
                                        >
                                        <span v-if="orgLearnerCounts && organizationDetails.pricing_tier === 'teams'">
                                            <span class="badge badge-gray font-weight-500 mr-1">
                                                <span class="">
                                                    {{ orgLearnerCounts.monthly_learner_counts.tracked }}/{{
                                                        organizationDetails.max_learner_users
                                                    }}
                                                </span>
                                                <span class="mr-1">Tracked Learners this Month</span>
                                                <span
                                                    class="text-secondary"
                                                    v-show="orgLearnerCounts.monthly_learner_counts.all"
                                                    >({{ orgLearnerCounts.monthly_learner_counts.all }} Total)</span
                                                >
                                            </span>
                                            <help-text
                                                content="The number of learners that accessed resources with reporting enabled. Resets monthly."
                                                placement="top"
                                                class="align-middle"
                                            ></help-text>
                                        </span>
                                        <span v-else-if="orgLearnerCounts && organizationDetails.pricing_tier === 'pro'">
                                            <span class="badge badge-gray font-weight-500 mr-1">
                                                <span class="">
                                                    {{ orgLearnerCounts.annual_learner_counts.tracked }}/{{
                                                        organizationDetails.max_learner_users
                                                    }}
                                                </span>
                                                <span class="mr-1">Tracked Learners this Year</span>
                                            </span>
                                            <help-text
                                                content="The number of learners that accessed resources with reporting enabled."
                                                placement="top"
                                                class="align-middle"
                                            ></help-text>
                                        </span>
                                    </div>
                                </div>
                            </template>
                        </base-table>
                    </div>
                    <div class="col-12" v-show="userTypeFilter === 'learners'">
                        <base-table
                            :table-data="learners"
                            :config="learnerTableConfig"
                            @search="updateSearchQuery($event)"
                            empty-message="No learners to display."
                            :server-side-search-mode="true"
                            :filtersApplied.sync="filtersApplied"
                            :loading="searchLoading && !loading"
                            :pageStart.sync="pageIndexStart"
                            :pageEnd.sync="pageIndexEnd"
                            :sortColumn.sync="sortColumn"
                            :sortDescend.sync="sortDescend"
                            :server-side-total-results="learnerTotalSearchResults"
                            :rows-per-page.sync="memberRowsPerPage"
                            :rows-per-page-options="[10, 50, 100]"
                            :max-pages="20"
                            ref="learnerTable"
                        >
                        </base-table>
                    </div>
                </div>

                <br />
                <hr />
                <br />

                <!-- Author Group Table -->
                <div class="row">
                    <div class="col-12">
                        <h1 class="mb-2 font-weight-bolder h4">Author Teams</h1>
                        <p class="text-secondary mb-2">
                            Create teams for authors that will share access to the same scenarios.
                        </p>
                    </div>
                </div>
                <br />
                <div class="row">
                    <div class="col-12">
                        <base-table
                            :table-data="authorGroups"
                            :config="groupTableConfig"
                            v-on:delete="openDeleteGroup($event)"
                            v-on:edit="openEditGroup($event)"
                            empty-message="No authoring teams created yet."
                            :rows-per-page="10"
                        >
                            <template slot="actions">
                                <button
                                    @click="openCreateGroup()"
                                    :disabled="!userIsAdmin"
                                    type="button"
                                    class="btn btn-sm btn-primary"
                                >
                                    Create Team
                                </button>
                            </template>
                        </base-table>
                    </div>
                </div>
                <br />
            </div>
        </div>

        <!-- Create Group Modal -->
        <base-modal id="create-group-modal" :loading="modalLoading" :centered="true">
            <template slot="title"> New Team </template>
            <template>
                <base-form
                    v-model="newGroup"
                    :fields="groupFormFields"
                    :inline-style="true"
                    ref="createGroupForm"
                ></base-form>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button @click="createGroup()" :disabled="modalLoading" class="btn btn-primary" type="button">
                    Create
                </button>
            </template>
        </base-modal>

        <!-- Edit Group Modal -->
        <base-modal id="edit-group-modal" :loading="modalLoading" :centered="true">
            <template slot="title"> Edit Team </template>
            <template>
                <base-form
                    v-model="selectedItem"
                    :fields="groupFormFields"
                    :inline-style="true"
                    ref="editGroupForm"
                ></base-form>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button @click="editGroup()" :disabled="modalLoading" class="btn btn-primary" type="button">
                    Save
                </button>
            </template>
        </base-modal>

        <!-- Delete Group Modal -->
        <base-modal id="delete-group-modal" :loading="modalLoading" :centered="true">
            <template slot="title"> Remove Team </template>
            <template>
                <p v-show="selectedItem" class="mb-0">
                    Remove team <strong>{{ selectedItem.name }}</strong
                    >?
                </p>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button @click="deleteGroup()" :disabled="modalLoading" class="btn btn-danger" type="button">
                    Remove
                </button>
            </template>
        </base-modal>

        <!-- Assign to Group Modal -->
        <base-modal id="assign-members-modal" :loading="modalLoading" :centered="true">
            <template slot="title">
                <span v-show="assignToGroup">Add Users to Author Team</span>
                <span v-show="!assignToGroup">Remove Users from Author Team</span>
            </template>
            <template>
                <div>
                    <div>
                        <p class="mb-2">
                            <span v-show="assignToGroup">Assign users to:</span>
                            <span v-show="!assignToGroup">Remove users from:</span>
                        </p>
                    </div>
                    <base-search
                        placeholder="Search for a team..."
                        v-model="selectedGroupID"
                        :options="authorGroups"
                        value-field="id"
                        label-field="name"
                        :search-fields="['name']"
                        :required="true"
                    ></base-search>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button @click="assignMembers()" :disabled="modalLoading" class="btn btn-primary" type="button">
                    <span v-show="assignToGroup">Assign</span>
                    <span v-show="!assignToGroup">Remove</span>
                </button>
            </template>
        </base-modal>

        <!-- Edit Member Modal -->
        <base-modal id="edit-member-modal" :loading="modalLoading" :centered="true">
            <template slot="title"> Edit User </template>
            <template>
                <div v-show="selectedItem.status === 'Pending'" class="mb-3">
                    <span class="badge badge-warning">Invitation Pending</span>
                </div>
                <base-form
                    v-model="selectedItem"
                    :fields="editMemberFormFields"
                    :inline-style="true"
                    ref="editMemberForm"
                ></base-form>
                <div v-show="selectedItem.status === 'Pending'" class="row">
                    <div class="col-12">
                        <div v-show="invitationSendSuccess">
                            <p class="mb-0 small">
                                <span
                                    class="icon icon-sm icon-glyphicons-basic-circle-empty-check text-success mr-1"
                                ></span>
                                Invitation sent!
                            </p>
                        </div>
                        <div v-show="!invitationSendSuccess">
                            <button
                                @click="resendInvitation()"
                                :disabled="invitationSendLoading"
                                class="btn btn-primary btn-sm"
                                role="button"
                            >
                                <span class="icon icon-sm icon-glyphicons-basic-envelope mr-1"></span>
                                <span>Resend Email Invitation</span>
                                <span class="ml-2" v-show="invitationSendLoading">
                                    <span
                                        class="spinner-border spinner-border-sm"
                                        role="status"
                                        aria-hidden="true"
                                    ></span>
                                    <span class="sr-only">Loading...</span>
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button @click="editMember()" :disabled="modalLoading" class="btn btn-primary" type="button">
                    Save
                </button>
            </template>
        </base-modal>

        <!-- Delete Member Modal -->
        <base-modal id="delete-member-modal" :loading="modalLoading" :centered="true">
            <template slot="title"> Remove User </template>
            <template>
                <p class="mb-0">
                    Remove user <strong v-if="selectedItem.user">{{ selectedItem.user.email }}</strong
                    >?
                </p>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button @click="deleteMember()" :disabled="modalLoading" class="btn btn-danger" type="button">
                    Remove
                </button>
            </template>
        </base-modal>

        <!-- Create Member Modal -->
        <base-modal id="create-member-modal" :loading="modalLoading" :centered="true">
            <template slot="title"> Add Team Member </template>
            <template>
                <div v-show="legacyUserCapacityReached || creatorCapacityReached">
                    <div class="mb-2">
                        <p v-if="legacyUserCapacityReached">
                            <span class="icon icon-glyphicons-basic-circle-empty-alert text-warning"></span>
                            Your organization has reached its capacity of
                            <span class="font-weight-bolder">{{ organizationDetails.max_users }}</span> users. To
                            increase your organization's user capacity, please get in touch with our team at
                            <a href="mailto:support@affinitylearning.ca?subject=Support Request - Account Creation"
                                >support@affinitylearning.ca</a
                            >.
                        </p>
                        <div v-else>
                            <p>
                                <span class="icon icon-glyphicons-basic-circle-empty-alert text-warning"></span>
                                <span v-if="creatorCapacityReached">
                                    You've reached your workspace's capacity of
                                    <span class="font-weight-bolder">{{ organizationDetails.max_creator_users }}</span>
                                    author users.
                                </span>
                                <span class="text-center">
                                    To add more team members,
                                    <span v-if="userIsAdmin && organizationBillingEnabled">
                                        <a href="#" @click="$refs.billingView.open()" data-dismiss="modal"
                                            >upgrade your plan</a
                                        >.
                                    </span>
                                    <span v-else>
                                        reach out to us at
                                        <a href="mailto:support@affinitylearning.ca"> support@affinitylearning.ca</a
                                        >.</span
                                    >
                                </span>
                            </p>
                            <support-request-prompt />
                        </div>
                    </div>
                    <hr />
                </div>
                <p>
                    Enter some information about the new user below. New users will receive an onboarding email to
                    create their account.
                </p>
                <p class="text-danger" v-show="createMemberError">
                    {{ createMemberError }}
                </p>
                <base-form
                    v-model="newMember"
                    :fields="createMemberFormFields"
                    :inline-style="true"
                    ref="createMemberForm"
                ></base-form>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    @click="createMember()"
                    :disabled="modalLoading || userCapacityReached"
                    class="btn btn-primary"
                    type="button"
                >
                    <span class="icon icon-glyphicons-basic-envelope"></span>
                    Send Invitation
                </button>
            </template>
        </base-modal>

        <!-- Bulk Actions Modal -->
        <base-modal id="member-status-modal" :loading="modalLoading" :centered="true">
            <template slot="title"> Bulk Status Update </template>
            <template>
                <p>
                    Update the status of <strong>{{ selectedMembers.length }}</strong> users to:
                </p>
                <div>
                    <div class="custom-control custom-radio mb-2">
                        <input
                            type="radio"
                            id="bulk-status-active"
                            class="custom-control-input"
                            v-model="bulkActionStatus"
                            value="active"
                        />
                        <label class="custom-control-label bulk-action-label" for="bulk-status-active">
                            <span class="badge badge-success">Active</span>
                        </label>
                        <div class="ml-1 text-secondary small">Activate these user accounts.</div>
                    </div>
                    <div class="custom-control custom-radio mb-2">
                        <input
                            type="radio"
                            id="bulk-status-deactivated"
                            class="custom-control-input"
                            v-model="bulkActionStatus"
                            value="deactivated"
                        />
                        <label class="custom-control-label bulk-action-label mr-1" for="bulk-status-deactivated">
                            <span class="badge badge-gray">Deactivated</span>
                        </label>
                        <div class="ml-1 text-secondary small">Temporarily remove access for these users.</div>
                    </div>
                    <div class="custom-control custom-radio mb-2">
                        <input
                            type="radio"
                            id="bulk-status-removed"
                            class="custom-control-input"
                            v-model="bulkActionStatus"
                            value="removed"
                        />
                        <label class="custom-control-label bulk-action-label mr-1" for="bulk-status-removed">
                            <span class="badge badge-danger">Removed</span>
                        </label>
                        <div class="ml-1 text-secondary small">Remove these users from your workspace.</div>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    @click="bulkUpdateMemberStatus()"
                    :disabled="modalLoading"
                    class="btn btn-primary"
                    type="button"
                >
                    Update
                </button>
            </template>
        </base-modal>

        <!-- Upload Members Modal -->
        <base-modal id="member-upload-modal" :loading="modalLoading" :centered="true">
            <template slot="title"> Bulk Upload Team Members </template>
            <template>
                <p>Upload a list of users to be added to your workspace.</p>
                <!-- Download Template File -->
                <div class="mb-1">
                    <form method="GET" action="/api/members/upload/">
                        <button class="btn btn-gray btn-sm" type="submit">
                            <span class="icon icon-lg icon-glyphicons-basic-cloud-download icon-mr"></span>
                            <span>Download Template File</span>
                        </button>
                    </form>
                </div>
                <hr />
                <file-upload
                    v-model="bulkMemberCreateCSV"
                    :replaceable="true"
                    placeholder="Drop your upload file here"
                    accept=".csv"
                ></file-upload>
                <p class="mt-3 mb-2">
                    <span class="icon icon-glyphicons-basic-circle-alert text-primary"></span>
                    A maximum of 50 new users can be uploaded at once. Please wait until the upload process is complete
                    before closing your browser window or refreshing the page.
                </p>
                <p v-show="bulkUploadError" class="mt-3 mb-2 text-danger">
                    {{ bulkUploadError }}
                </p>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    @click="uploadMemberCSV()"
                    :disabled="modalLoading || !bulkMemberCreateCSV"
                    class="btn btn-primary"
                    type="button"
                >
                    Upload
                </button>
            </template>
        </base-modal>
        <billing-view ref="billingView"></billing-view>
    </div>
</template>

<script>
import debounce from "lodash.debounce"
import http from "@/axios.js"
import BaseTable from "@/components/BaseTable"
import BaseModal from "@/components/BaseModal"
import BaseForm from "@/components/BaseForm"
import BaseSearch from "@/components/BaseSearch"
import Utilities from "@/assets/js/utilities.js"
import FileUpload from "@/components/FileUpload"
import BillingView from "@/components/BillingView"
import SupportRequestPrompt from "@/components/SupportRequestPrompt"
import HelpText from "@/components/HelpText"

export default {
    name: "MemberList",
    components: {
        BaseTable,
        BaseModal,
        BaseForm,
        BaseSearch,
        FileUpload,
        BillingView,
        SupportRequestPrompt,
        HelpText,
    },
    data() {
        return {
            members: [],
            learners: [],
            authorGroups: [],
            groupFormFields,
            newGroup: {},
            newMember: {},
            selectedGroupID: null,
            selectedItem: {},
            storedMember: {},
            selectedMembers: [],
            modalLoading: false,
            createMemberError: null,
            assignToGroup: true,
            invitationSendLoading: false,
            invitationSendSuccess: false,
            sortColumn: "created",
            sortDescend: true,
            searchLoading: false,
            searchQuery: "",
            memberTotalSearchResults: null,
            learnerTotalSearchResults: null,
            pageIndexStart: 0,
            pageIndexEnd: 10,
            memberRowsPerPage: 10,
            filtersApplied: {},
            userTypeFilter: "team",
            bulkActionStatus: "active",
            bulkMemberCreateCSV: null,
            bulkUploadError: null,
            orgLearnerCounts: null,
        }
    },
    computed: {
        loading() {
            return this.$store.getters.loading
        },
        userIsAdmin() {
            return this.$store.getters.userRole === "admin"
        },
        currentUserId() {
            return this.$store.getters.user.id
        },
        showBulkUserActions() {
            return this.selectedMembers.length > 0
        },
        currentOrganization() {
            return this.$store.getters.currentOrganization
        },
        organizationLMSAuthConfigured() {
            return this.$store.getters.organizationLMSAuthConfigured
        },
        lmsInstances() {
            return this.$store.getters.organizationLMSInstances.filter((i) => i.is_usable)
        },
        organizationBillingEnabled() {
            return this.$store.getters.organizationBillingEnabled
        },
        memberTableConfig() {
            return {
                initialSort: "Created",
                initialSortDescend: true,
                actions: {
                    edit: {
                        disabled: !this.userIsAdmin,
                    },
                    delete: {
                        disabled: !this.userIsAdmin,
                    },
                },
                filters: [
                    {
                        label: "Status",
                        field: "status",
                        options: [
                            {
                                id: "pending,active",
                                name: "Pending & Active",
                            },
                            {
                                id: "active",
                                name: "Active",
                            },
                            {
                                id: "pending",
                                name: "Pending",
                            },
                            {
                                id: "deactivated",
                                name: "Deactivated",
                            },
                        ],
                        valueField: "id",
                        labelField: "name",
                    },
                    {
                        label: "Team",
                        field: "teams",
                        options: this.authorGroups,
                        valueField: "id",
                        labelField: "name",
                    },
                ],
                multiSelect: true,
                columns: [
                    {
                        name: "Email",
                        key: "user.email",
                        sort: true,
                        display: (row) => {
                            return row.user.email
                        },
                    },
                    {
                        name: "Name",
                        sort: false,
                        display: (row) => {
                            if (row.first_name && row.last_name) {
                                return [row.first_name, row.last_name].join(" ")
                            } else {
                                return row.first_name || row.last_name || ""
                            }
                        },
                    },
                    {
                        name: "Role",
                        sort: true,
                        key: "role",
                        type: "list",
                        display: (elem) => {
                            return elem
                        },
                        elements: (row) => {
                            if (row.user.is_application_admin) {
                                return ["Affinity Team"]
                            }
                            return [roleNames[row.role]]
                        },
                        listClass() {
                            return "badge-gray"
                        },
                    },
                    {
                        name: "Created",
                        sort: true,
                        type: "date",
                        sortKey: "created",
                        key: "created",
                        display: (row) => {
                            return Utilities.formatDateTime(row.created)
                        },
                    },
                    {
                        name: "Status",
                        sort: false,
                        sortKey: "status",
                        type: "list",
                        clickable: false,
                        listClass: (elem, row) => {
                            let badgeClass = "badge-success"
                            if (row.user.is_application_admin) {
                                return "badge-highlight text-success"
                            }
                            switch (elem) {
                                case "Pending":
                                    badgeClass = "badge-warning"
                                    break
                                case "Deactivated":
                                    badgeClass = "badge-gray-darker"
                                    break
                            }
                            return badgeClass
                        },
                        display: (elem) => {
                            return elem
                        },
                        elements: (row) => {
                            return [row.status]
                        },
                    },
                    {
                        name: "Teams",
                        sort: false,
                        key: "groups",
                        type: "list",
                        clickable: false,
                        listClass: () => "badge-gray",
                        display: (elem) => {
                            return elem.group.name
                        },
                        elements: (row) => {
                            return row.groups.filter((g) => g.group.group_type === "author")
                        },
                    },
                ],
            }
        },
        learnerTableConfig() {
            return {
                initialSort: "First Access",
                initialSortDescend: true,
                multiSelect: false,
                columns: [
                    {
                        name: "Email",
                        key: "email",
                        sort: true,
                        link: (row) => {
                            return `/users/${row.id}`
                        },
                        display: (row) => {
                            return row.email
                        },
                        classes: "pl-1 py-1",
                    },
                    {
                        name: "Learner User ID",
                        sort: false,
                        key: "lms_user_id",
                        classes: "badge badge-light",
                    },
                    {
                        name: "From LMS",
                        sort: true,
                        key: "lms_instance.name",
                        display: (elem) => {
                            if (elem.lms_instance) {
                                return elem.lms_instance.name
                            }
                            return ""
                        },
                        classes: "badge badge-gray",
                    },
                    {
                        name: "First Access",
                        sort: true,
                        type: "date",
                        sortKey: "created",
                        key: "created",
                        display: (row) => {
                            return Utilities.formatDateTime(row.created)
                        },
                    },
                    {
                        name: "Last Access",
                        sort: true,
                        type: "date",
                        sortKey: "last_lti_launch",
                        key: "last_lti_launch",
                        display: (row) => {
                            return Utilities.formatDateTime(row.last_lti_launch)
                        },
                    },
                ],
                filters: [
                    {
                        label: "LMS",
                        field: "lms_instance",
                        options: this.lmsInstances,
                        valueField: "id",
                        labelField: "name",
                    },
                ],
            }
        },
        groupTableConfig() {
            return {
                initialSort: "Created",
                initialSortDescend: true,
                actions: {
                    edit: {
                        disabled: !this.userIsAdmin,
                    },
                    delete: {
                        disabled: !this.userIsAdmin,
                    },
                },
                columns: [
                    {
                        name: "Name",
                        key: "name",
                        sort: true,
                        classes: "badge badge-gray",
                    },
                    {
                        name: "Created",
                        key: "created",
                        sort: true,
                        sortKey: "created",
                        type: "date",
                        display: (row) => {
                            return Utilities.formatDateTime(row.created)
                        },
                    },
                    {
                        name: "Member Count",
                        key: "member_count",
                        sort: true,
                    },
                ],
            }
        },
        organizationDetails() {
            return this.$store.getters.organizationDetails
        },
        legacyUserMgmtActive() {
            return this.organizationDetails.user_management_mode === "legacy"
        },
        legacyUserCapacityReached() {
            if (!this.legacyUserMgmtActive) {
                return false
            }
            return this.organizationDetails.active_user_count >= this.organizationDetails.max_users
        },
        creatorCapacityReached() {
            if (this.legacyUserMgmtActive) {
                return false
            }
            return this.organizationDetails.active_creator_count >= this.organizationDetails.max_creator_users
        },
        userCapacityReached() {
            return this.legacyUserCapacityReached || this.creatorCapacityReached
        },
        legacyUserSeatsRemaining() {
            let availableSeats = this.organizationDetails.max_users - this.organizationDetails.active_user_count
            if (availableSeats < 0) {
                return 0
            }
            return availableSeats
        },
        freeTierActive() {
            return this.$store.getters.organizationDetails.free_tier_active
        },
        memberActiveOptionDisabled() {
            // Active option should be disabled if capacity has been reached, member is a creator,
            // and they're not already active.
            return (
                this.creatorCapacityReached &&
                ["admin", "author"].includes(this.storedMember.role) &&
                !this.storedMember.is_active
            )
        },
        userRoles() {
            return [
                {
                    value: "author",
                    label: roleNames["author"],
                    subtext: "Ability to create scenarios and send them to learners.",
                    disabled: !this.legacyUserMgmtActive && this.creatorCapacityReached && !this.selectedItem.id,
                },
                {
                    value: "admin",
                    label: roleNames["admin"],
                    subtext: "Access to everything in this workspace, with the ability to manage users.",
                    disabled: !this.legacyUserMgmtActive && this.creatorCapacityReached && !this.selectedItem.id,
                },
            ]
        },
        createMemberFormFields() {
            return [
                {
                    name: "Role",
                    key: "role",
                    type: "radio",
                    required: true,
                    options: this.userRoles,
                },
                {
                    name: "First Name",
                    placeholder: "First Name (optional)",
                    key: "first_name",
                    type: "text",
                    required: false,
                },
                {
                    name: "Last Name",
                    placeholder: "Last Name (optional)",
                    key: "last_name",
                    type: "text",
                    required: false,
                },
                {
                    name: "Email",
                    key: "email",
                    type: "text",
                    required: true,
                },
            ]
        },
        editMemberFormFields() {
            let vm = this
            return [
                {
                    name: "Role",
                    key: "role",
                    type: "radio",
                    required: true,
                    options: this.userRoles,
                },
                {
                    name: "First Name",
                    placeholder: "First Name (optional)",
                    key: "first_name",
                    type: "text",
                    required: false,
                },
                {
                    name: "Last Name",
                    placeholder: "Last Name (optional)",
                    key: "last_name",
                    type: "text",
                    required: false,
                },
                {
                    name: "Status",
                    key: "is_active",
                    type: "radio",
                    options: [
                        {
                            key: "Active",
                            label: "Active",
                            value: true,
                            disabled() {
                                return vm.memberActiveOptionDisabled
                            },
                        },
                        {
                            key: "Deactivated",
                            label: "Deactivated",
                            value: false,
                        },
                    ],
                    required: false,
                    description: "Deactivated users will no longer have access to your organization.",
                },
            ]
        },
    },
    watch: {
        sortColumn: function () {
            this.refreshMembers()
        },
        sortDescend: function () {
            this.refreshMembers()
        },
        pageIndexStart: function () {
            this.refreshMembers()
        },
        pageIndexEnd: function () {
            this.refreshMembers()
        },
        memberRowsPerPage: function () {
            this.pageIndexStart
        },
        filtersApplied: {
            handler() {
                this.refreshMembers()
            },
            deep: true,
        },
    },
    methods: {
        async fetchInitialData() {
            // Fetching auth details makes sure that the displayed organization matches the
            // users stored selected organization, to limit the chance of a mismatch between the
            // displayed users and the displayed org name.
            this.$store.dispatch("addLoad")
            await this.$store.dispatch("fetchAuth")
            this.fetchData()
            this.$store.dispatch("removeLoad")
        },
        // Fetch data for view.
        async fetchData() {
            this.$store.dispatch("addLoad")
            let vm = this
            this.fetchMembers().then(() => vm.$store.dispatch("removeLoad"))
            this.$store.dispatch("addLoad")
            http.get("/api/groups/")
                .then(function (response) {
                    vm.authorGroups = response.data
                })
                .finally(function () {
                    vm.$store.dispatch("removeLoad")
                })

            // Enable or disable the admin action buttons depending on the user's role.
            this.memberTableConfig.actions.edit.disabled = !this.userIsAdmin
            this.memberTableConfig.actions.delete.disabled = !this.userIsAdmin

            this.$store.dispatch("addLoad")
            await this.$store.dispatch("fetchOrganizationDetails").then(() => vm.$store.dispatch("removeLoad"))
            // Only fetch and display learner counts for certain pricing tiers.
            if (["pro", "teams"].includes(this.organizationDetails.pricing_tier)) {
                http.get(`/api/organizations/${this.currentOrganization.id}/learner-counts/`).then((response) => {
                    vm.orgLearnerCounts = response.data
                })
            }
        },
        // Fetch the members list with the current search criteria.
        async fetchMembers() {
            let vm = this
            return new Promise((resolve) => {
                if (vm.userTypeFilter === "team") {
                    http.post("/api/members/search/", {
                        query: this.searchQuery,
                        start: this.pageIndexStart,
                        end: this.pageIndexEnd,
                        sort: this.sortColumn,
                        sortDescend: this.sortDescend,
                        filters: this.filtersApplied,
                        organizationID: this.currentOrganization.id,
                    })
                        .then(function (response) {
                            vm.members = response.data.members
                            vm.memberTotalSearchResults = response.data.total_results
                        })
                        .finally(function (response) {
                            resolve(response)
                        })
                } else {
                    http.post("/api/learner-users/search/", {
                        query: this.searchQuery,
                        start: this.pageIndexStart,
                        end: this.pageIndexEnd,
                        sort: this.sortColumn,
                        sortDescend: this.sortDescend,
                        filters: this.filtersApplied,
                        organizationID: this.currentOrganization.id,
                    })
                        .then(function (response) {
                            vm.learners = response.data.results
                            vm.learnerTotalSearchResults = response.data.total_results
                        })
                        .finally(function (response) {
                            resolve(response)
                        })
                }
            })
        },
        // Refresh the members list.
        refreshMembers() {
            this.searchLoading = true
            this.fetchMembersDebounced()
        },
        // Handle an update of the search text in the members table.
        updateSearchQuery(query) {
            this.searchLoading = true
            this.searchQuery = query
            this.fetchMembersDebounced()
        },
        // Debounced member fetching, used to limit the amount of API hits while
        // the user updates search criteria.
        fetchMembersDebounced: debounce(async function () {
            await this.fetchMembers()
            this.searchLoading = false
        }, 400),
        // Open the create group modal.
        openCreateGroup() {
            this.$refs.createGroupForm.resetValidate()
            this.newGroup = {
                group_type: "author",
            }
            $("#create-group-modal").modal("show")
        },
        // Open the edit group modal.
        openEditGroup(group) {
            this.$refs.editGroupForm.resetValidate()
            this.selectedItem = Object.assign({}, group)
            $("#edit-group-modal").modal("show")
        },
        // Open the delete group modal.
        openDeleteGroup(group) {
            this.selectedItem = group
            $("#delete-group-modal").modal("show")
        },
        // Open the edit member modal.
        openCreateMember() {
            this.$refs.createMemberForm.resetValidate()
            this.newMember = {
                send_email: true,
                role: "author",
            }
            this.selectedItem = {}
            this.createMemberError = null
            $("#create-member-modal").modal("show")
        },
        // Open the edit member modal.
        openEditMember(member) {
            this.invitationSendSuccess = false
            this.$refs.editMemberForm.resetValidate()
            // Store a copy of the member.
            this.storedMember = Object.assign({}, member)
            this.selectedItem = Object.assign({}, member)
            $("#edit-member-modal").modal("show")
        },
        // Open the delete member modal.
        openDeleteMember(member) {
            this.selectedItem = member
            $("#delete-member-modal").modal("show")
        },
        // Open the assign to group modal.
        openAddToGroup(assignToGroup) {
            this.assignToGroup = assignToGroup
            this.selectedGroupID = null
            $("#assign-members-modal").modal("show")
        },
        // Open the member status modal.
        openStatusModal() {
            this.bulkActionStatus = "active"
            $("#member-status-modal").modal("show")
        },
        // Open the member upload modal.
        openUploadModal() {
            if (this.userCapacityReached) {
                $("#cap-reached-modal").modal("show")
                return
            }
            this.bulkUploadError = null
            $("#member-upload-modal").modal("show")
        },
        // Upload the member creation csv.
        uploadMemberCSV() {
            let vm = this
            this.modalLoading = true

            let formData = new FormData()
            formData.append("organizationID", this.currentOrganization.id)
            formData.append("csv", this.bulkMemberCreateCSV.file)

            http.post("/api/members/upload/", formData)
                .then(function () {
                    $("#member-upload-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Users created.",
                        data: {},
                    })
                    vm.bulkMemberCreateCSV = null
                    vm.fetchData()
                })
                .catch(function (error) {
                    vm.bulkUploadError = `Formatting Error: ${error.response.data}`
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        // Update the status of a list of members.
        bulkUpdateMemberStatus() {
            let vm = this
            this.modalLoading = true
            let selectedMemberIDs = this.selectedMembers.map((member) => member.id)
            http.put("/api/members/status/", {
                memberIDs: selectedMemberIDs,
                status: this.bulkActionStatus,
            })
                .then(function () {
                    $("#member-status-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Users updated.",
                        data: {},
                    })
                    vm.fetchData()
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        // Assign/unassign members to/from a group.
        assignMembers() {
            let vm = this
            this.modalLoading = true
            let selectedMemberIDs = this.selectedMembers.map((member) => member.id)
            http.put("/api/groups/" + this.selectedGroupID + "/assign/", {
                memberIDs: selectedMemberIDs,
                status: this.assignToGroup,
            })
                .then(function () {
                    $("#assign-members-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Team members updated.",
                        data: {},
                    })
                    vm.fetchData()
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        // Edit an existing member.
        editMember() {
            let vm = this
            this.modalLoading = true
            if (!this.$refs.editMemberForm.validate()) {
                return
            }
            http.put("/api/members/" + this.selectedItem.id + "/", this.selectedItem)
                .then(function () {
                    $("#edit-member-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "User updated.",
                        data: {},
                    })
                    vm.fetchData()
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        // Delete an existing member.
        deleteMember() {
            let vm = this
            this.modalLoading = true
            http.put("/api/members/" + this.selectedItem.id + "/remove/")
                .then(function () {
                    $("#delete-member-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "User removed.",
                        data: {},
                    })
                    vm.fetchData()
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        // Create a new member.
        createMember() {
            if (!this.$refs.createMemberForm.validate()) {
                return
            }
            this.modalLoading = true
            let vm = this
            http.post("/api/members/", {
                ...this.newMember,
                organizationID: this.currentOrganization.id,
            })
                .then(function (response) {
                    vm.modalLoading = false
                    $("#create-member-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "User added.",
                        data: {},
                    })
                    vm.fetchData()
                })
                .catch(function (error) {
                    if (error.response.data.user) {
                        vm.createMemberError = error.response.data.user
                    } else {
                        vm.createMemberError = null
                    }
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        // Create a new group.
        createGroup() {
            this.modalLoading = true
            if (!this.$refs.createGroupForm.validate()) {
                return
            }
            let vm = this
            http.post("/api/groups/", this.newGroup)
                .then(function () {
                    $("#create-group-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Group created.",
                        data: {},
                    })
                    vm.fetchData()
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        // Edit an existing group.
        editGroup() {
            this.modalLoading = true
            if (!this.$refs.editGroupForm.validate()) {
                return
            }
            let vm = this
            http.put("/api/groups/" + this.selectedItem.id + "/", this.selectedItem)
                .then(function () {
                    $("#edit-group-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Group updated.",
                        data: {},
                    })
                    vm.fetchData()
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        // Delete an existing group.
        deleteGroup() {
            this.modalLoading = true
            let vm = this
            http.put("/api/groups/" + this.selectedItem.id + "/remove/")
                .then(function () {
                    $("#delete-group-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Group removed.",
                        data: {},
                    })
                    vm.fetchData()
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        // Resend an invitation to a pending member.
        resendInvitation() {
            this.invitationSendLoading = true
            let vm = this
            http.post("/api/members/" + this.selectedItem.id + "/resend-invitation/")
                .then(function () {
                    vm.invitationSendSuccess = true
                })
                .finally(function () {
                    vm.invitationSendLoading = false
                })
        },
        // Set the value of the creators-only filter. Allows the user to toggle between
        // their "Team" and "All" members of their organization.
        setUserTypeFilter(filter) {
            if (this.userTypeFilter !== filter) {
                this.refreshMembers()
            }
            this.userTypeFilter = filter
            this.$refs.memberTable.resetCurrentPage()
            // Store the state of the filter in the URL for better navigation.
            let route = {
                name: "MemberList",
                query: {
                    filter: filter || "all",
                },
            }
            this.$router.push(route).catch((error) => {
                if (error.name !== "NavigationDuplicated") {
                    console.error(error)
                }
            })
        },
    },
    created() {
        this.fetchInitialData()
        // Set the initial state of the creators-only filter.
        if (this.$route.query.filter === "all") {
            this.userTypeFilter = null
        } else if (!this.$route.query.filter) {
            // Default to the team filter.
            this.userTypeFilter = "team"
        } else {
            this.userTypeFilter = this.$route.query.filter
        }
    },
}

const roleNames = {
    student: "Learner",
    author: "Author",
    admin: "Admin",
}

const groupFormFields = [
    {
        name: "Name",
        key: "name",
        type: "text",
        required: true,
    },
]
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.user-table-col {
    max-width: 1600px;
    @media (min-width: $breakpoint-sm) {
        padding-left: 30px;
        padding-right: 30px;
    }
    @media (min-width: $breakpoint-md) {
        padding-left: 40px;
        padding-right: 40px;
    }
    @media (min-width: $breakpoint-lg) {
        padding-left: 60px;
        padding-right: 60px;
    }
    @media (min-width: $breakpoint-xl) {
        padding-left: 90px;
        padding-right: 90px;
    }
}

.bulk-action-label {
    margin-bottom: 2px;
}
</style>